import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';

import { findPhaseScheduleItem } from '../../functions';
import { changePercentageItemByPercent } from '../../gantChangeData';
import { RootState } from '../../store';
import { ItemType, MappingItemIdIndexType } from '../../types';

const useControlParent = (
    values: ItemType,
    bufferItems: React.MutableRefObject<ItemType[]>,
    saveBufferToStore: () => void,
    moveItems: (itemsForMoving: ItemType[], newItems: ItemType[]) => ItemType[],
    historyDeletedChildren: Set<string>,
    updatedItem?: ItemType,
) => {
    const environment = useRelayEnvironment();
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const currentGantItem = useSelector<RootState, ItemType | null>((state) => state.gantItems.currentGantItem);

    const controlParent = (
        previousItems: ItemType[],
        nextParentId?: string | null,
        previousParentId?: string | null,
    ) => {
        const itemsForMoving: ItemType[] = [];

        const parentIds = new Set([nextParentId, previousParentId]);

        let newItems = previousItems.map((item) => {
            if (!parentIds.has(item.id)) {
                return item;
            }

            const newItem = { ...item };

            itemsForMoving.push(newItem);

            if (nextParentId === item.id) {
                const childrenSet = new Set(newItem?.children);

                const newChildren = childrenSet.has(currentGantItem?.id!)
                    ? newItem?.children || []
                    : [...(newItem.children || []), currentGantItem?.id!];

                newItem.children = [...newChildren, ...historyDeletedChildren];
                return newItem;
            }

            const newChildren = (newItem.children || []).filter((childId) => childId !== currentGantItem?.id!);
            newItem.children = [...newChildren, ...historyDeletedChildren];
            return newItem;
        });

        newItems = moveItems(itemsForMoving, newItems);

        if (nextParentId) {
            newItems = changePercentageItemByPercent(
                mappingItemIdIndex,
                currentGantItem?.completionPercentage!,
                currentGantItem!,
                newItems,
            );
        }

        return newItems;
    };

    useEffect(() => {
        if (!values || !updatedItem || !values.parentId) {
            return;
        }

        const previousItem = bufferItems.current[mappingItemIdIndex[updatedItem.id]];

        if (values.parentId === previousItem.parentId) {
            return;
        }

        bufferItems.current = [...bufferItems.current];
        bufferItems.current[mappingItemIdIndex[updatedItem.id]] = {
            ...previousItem,
            phaseId: findPhaseScheduleItem(values.parentId, environment) || undefined,
            parentId: values.parentId,
        } as ItemType;

        bufferItems.current = controlParent(bufferItems.current, values.parentId, previousItem.parentId);
        saveBufferToStore();
    }, [values?.parentId]);

    return [];
};

export default useControlParent;
